import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Cookie, X, Check, ShieldCheck, Settings2, Info } from 'lucide-react';
import { Link } from 'react-router-dom';

const CookieConsent = () => {
    const { t } = useTranslation('marketing');
    const [isVisible, setIsVisible] = useState(false);
    const [showOptions, setShowOptions] = useState(false);
    const [consentState, setConsentState] = useState({
        essential: true,
        analytics: true,
        marketing: true
    });

    useEffect(() => {
        const consent = localStorage.getItem('gymtracker_cookie_consent');
        if (!consent) {
            const timer = setTimeout(() => setIsVisible(true), 1500);
            return () => clearTimeout(timer);
        }

        const handleShow = () => setIsVisible(true);
        window.addEventListener('showCookieConsent', handleShow);
        return () => window.removeEventListener('showCookieConsent', handleShow);
    }, []);

    const handleAcceptAll = () => {
        localStorage.setItem('gymtracker_cookie_consent', 'all');
        window.dispatchEvent(new Event('cookieConsentChanged'));
        setIsVisible(false);
    };

    const handleAcceptEssential = () => {
        localStorage.setItem('gymtracker_cookie_consent', 'essential');
        window.dispatchEvent(new Event('cookieConsentChanged'));
        setIsVisible(false);
    };

    const handleSaveCustom = () => {
        localStorage.setItem('gymtracker_cookie_consent', JSON.stringify(consentState));
        window.dispatchEvent(new Event('cookieConsentChanged'));
        setIsVisible(false);
    };

    if (!isVisible) return null;

    return (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 w-[calc(100%-3rem)] md:w-full md:max-w-2xl z-[9999] animate-in fade-in zoom-in-95 duration-1000 ease-out">
            <div className="relative overflow-hidden bg-neutral-900/98 backdrop-blur-3xl border border-white/10 rounded-[2.5rem] p-8 md:p-12 shadow-[0_32px_120px_-12px_rgba(0,0,0,1)] ring-1 ring-white/20">
                {/* Decorative Elements */}
                <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-64 h-64 bg-red-600/20 blur-[100px] rounded-full pointer-events-none" />
                <div className="absolute -bottom-32 left-1/2 -translate-x-1/2 w-64 h-64 bg-blue-600/10 blur-[100px] rounded-full pointer-events-none" />

                <button
                    onClick={() => setIsVisible(false)}
                    aria-label="Cerrar"
                    className="absolute top-8 right-8 p-2 text-neutral-500 hover:text-white hover:bg-white/5 rounded-full transition-all z-20"
                >
                    <X size={20} />
                </button>

                <div className="relative z-10 flex flex-col items-center text-center">
                    <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-2xl shadow-inner mb-6">
                        <Cookie className="text-red-500" size={32} />
                    </div>

                    <h3 className="text-white font-black uppercase tracking-[0.3em] text-xs md:text-sm mb-6">
                        {t('cookies_policy.title')}
                    </h3>

                    {!showOptions ? (
                        <>
                            <p className="text-neutral-200 text-base md:text-lg leading-relaxed mb-10 font-medium max-w-lg">
                                {t('cookie_consent.message')}
                            </p>

                            <div className="w-full grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <button
                                    onClick={handleAcceptAll}
                                    className="sm:col-span-2 bg-red-600 hover:bg-red-500 text-white font-black py-5 px-8 rounded-2xl transition-all active:scale-[0.98] shadow-2xl shadow-red-600/30 flex items-center justify-center gap-3 text-base uppercase tracking-widest border border-red-500/50"
                                >
                                    <Check size={20} />
                                    {t('cookie_consent.accept')}
                                </button>

                                <button
                                    onClick={() => setShowOptions(true)}
                                    className="bg-white/5 hover:bg-white/10 text-white font-bold py-4 px-8 rounded-2xl border border-white/10 transition-all active:scale-[0.98] flex items-center justify-center gap-3 text-sm"
                                >
                                    <Settings2 size={18} className="text-neutral-400" />
                                    {t('cookie_consent.customize')}
                                </button>

                                <button
                                    onClick={handleAcceptEssential}
                                    className="bg-white/5 hover:bg-white/10 text-white font-bold py-4 px-8 rounded-2xl border border-white/10 transition-all active:scale-[0.98] flex items-center justify-center gap-3 text-sm"
                                >
                                    {t('cookie_consent.decline')}
                                </button>
                            </div>
                        </>
                    ) : (
                        <div className="w-full max-w-md mx-auto">
                            <div className="p-6 bg-white/5 rounded-3xl border border-white/10 space-y-6 mb-8 text-left">
                                <div className="flex items-center justify-between opacity-60">
                                    <div className="flex flex-col">
                                        <span className="text-white text-sm font-bold">{t('cookies_policy.essential_title')}</span>
                                        <span className="text-xs text-neutral-400">{t('cookie_consent.always_active')}</span>
                                    </div>
                                    <div className="w-10 h-5 bg-red-600 rounded-full flex items-center px-1">
                                        <div className="w-3.5 h-3.5 bg-white rounded-full translate-x-5" />
                                    </div>
                                </div>
                                <div className="flex items-center justify-between">
                                    <div className="flex flex-col">
                                        <span className="text-white text-sm font-bold">{t('cookies_policy.analytics_title')}</span>
                                        <span className="text-xs text-neutral-400">{t('cookie_consent.analytics_desc')}</span>
                                    </div>
                                    <button
                                        onClick={() => setConsentState(s => ({ ...s, analytics: !s.analytics }))}
                                        className={`w-10 h-5 rounded-full flex items-center px-1 transition-colors ${consentState.analytics ? 'bg-red-600' : 'bg-neutral-700'}`}
                                    >
                                        <div className={`w-3.5 h-3.5 bg-white rounded-full transition-transform ${consentState.analytics ? 'translate-x-5' : 'translate-x-0'}`} />
                                    </button>
                                </div>
                                <div className="flex items-center justify-between">
                                    <div className="flex flex-col">
                                        <span className="text-white text-sm font-bold">{t('cookies_policy.adsense_title')}</span>
                                        <span className="text-xs text-neutral-400">{t('cookie_consent.marketing_desc')}</span>
                                    </div>
                                    <button
                                        onClick={() => setConsentState(s => ({ ...s, marketing: !s.marketing }))}
                                        className={`w-10 h-5 rounded-full flex items-center px-1 transition-colors ${consentState.marketing ? 'bg-red-600' : 'bg-neutral-700'}`}
                                    >
                                        <div className={`w-3.5 h-3.5 bg-white rounded-full transition-transform ${consentState.marketing ? 'translate-x-5' : 'translate-x-0'}`} />
                                    </button>
                                </div>
                            </div>

                            <div className="flex gap-3">
                                <button
                                    onClick={handleSaveCustom}
                                    className="flex-[2] bg-red-600 hover:bg-red-500 text-white font-black py-4 rounded-2xl transition-all active:scale-[0.98] text-xs uppercase tracking-widest shadow-lg shadow-red-600/20 border border-red-500/50"
                                >
                                    {t('cookie_consent.save')}
                                </button>
                                <button
                                    onClick={() => setShowOptions(false)}
                                    className="flex-1 bg-white/5 text-white font-bold py-4 rounded-2xl border border-white/10 text-xs hover:bg-white/10 transition-all"
                                >
                                    {t('cookie_consent.back')}
                                </button>
                            </div>
                        </div>
                    )}

                    <div className="flex items-center justify-center gap-6 border-t border-white/10 mt-10 pt-8 w-full max-w-sm">
                        <Link
                            to="/cookies"
                            className="text-neutral-400 hover:text-red-400 text-[10px] uppercase font-black tracking-widest flex items-center gap-1.5 transition-colors group"
                        >
                            <ShieldCheck size={14} className="text-neutral-500 group-hover:text-red-500 transition-colors" />
                            {t('cookie_consent.policy')}
                        </Link>
                        <div className="w-1 h-1 bg-white/20 rounded-full" />
                        <Link
                            to="/privacy"
                            className="text-neutral-400 hover:text-red-400 text-[10px] uppercase font-black tracking-widest flex items-center gap-1.5 transition-colors group"
                        >
                            <Info size={14} className="text-neutral-500 group-hover:text-red-500 transition-colors" />
                            {t('gdpr.compliance')}
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CookieConsent;
